<template>
    <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
        <template v-if="bulkControls">
            <template v-if="!hideRequest">
                <ButtonGroup v-if="customActionsFor('request').length">
                    <Button type="is-info" :disabled="processing" :compact @click="requestAction('request')">
                        Request Approval
                    </Button>
                    <Menu :triggers="['click']">
                        <template #trigger>
                            <Button type="is-info" square :disabled="processing" :compact title="More request options">
                                <i class="mdi mdi-chevron-down"></i>
                            </Button>
                        </template>
                        <ul>
                            <li v-for="customAction in customActionsFor('request')" :key="customAction.label">
                                <button
                                    :disabled="customAction.disabled"
                                    @click="requestCustomAction('request', customAction)"
                                >
                                    {{ customAction.label }}
                                </button>
                            </li>
                        </ul>
                    </Menu>
                    <Tooltip v-if="approvables[0].current_user_can_approve" label="Approve Instantly">
                        <Button type="is-success" :disabled="processing" :compact @click="requestAction('approve')">
                            <i class="mdi mdi-check-bold" />
                        </Button>
                    </Tooltip>
                </ButtonGroup>
                <Button v-else type="is-info" :disabled="processing" :compact @click="requestAction('request')">
                    Request Approval
                </Button>
            </template>
            <template v-if="!hideActions">
                <ButtonGroup v-if="customActionsFor('approve').length">
                    <Button type="is-success" :disabled="processing" :compact @click="requestAction('approve')">
                        Approve
                    </Button>
                    <Menu :triggers="['click']">
                        <template #trigger>
                            <Button
                                type="is-success"
                                square
                                :disabled="processing"
                                :compact
                                title="More approval options"
                            >
                                <i class="mdi mdi-chevron-down"></i>
                            </Button>
                        </template>
                        <ul>
                            <li v-for="customAction in customActionsFor('approve')" :key="customAction.label">
                                <button
                                    :disabled="customAction.disabled"
                                    @click="requestCustomAction('approve', customAction)"
                                >
                                    {{ customAction.label }}
                                </button>
                            </li>
                        </ul>
                    </Menu>
                </ButtonGroup>
                <Button v-else type="is-success" :compact :disabled="processing" @click="requestAction('approve')">
                    Approve
                </Button>
            </template>
            <template v-if="!hideActions">
                <ButtonGroup v-if="customActionsFor('reject').length">
                    <Button type="is-danger" :disabled="processing" :compact @click="requestAction('reject')">
                        Reject
                    </Button>
                    <Menu :triggers="['click']">
                        <template #trigger>
                            <Button
                                type="is-danger"
                                square
                                :disabled="processing"
                                :compact
                                title="More rejection options"
                            >
                                <i class="mdi mdi-chevron-down"></i>
                            </Button>
                        </template>
                        <ul>
                            <li v-for="customAction in customActionsFor('reject')" :key="customAction.label">
                                <button
                                    :disabled="customAction.disabled"
                                    @click="requestCustomAction('reject', customAction)"
                                >
                                    {{ customAction.label }}
                                </button>
                            </li>
                        </ul>
                    </Menu>
                </ButtonGroup>
                <Button v-else type="is-danger" :disabled="processing" :compact @click="requestAction('reject')">
                    Reject
                </Button>
            </template>
            <template v-if="!hideRequest">
                <ButtonGroup v-if="customActionsFor('reset').length">
                    <Button type="is-info" :disabled="processing" :compact @click="requestAction('reset')">
                        Reset To Draft
                    </Button>
                    <Menu :triggers="['click']">
                        <template #trigger>
                            <Button type="is-info" square :disabled="processing" :compact title="More reset options">
                                <i class="mdi mdi-chevron-down"></i>
                            </Button>
                        </template>
                        <ul>
                            <li v-for="customAction in customActionsFor('reset')" :key="customAction.label">
                                <button
                                    :disabled="customAction.disabled"
                                    @click="requestCustomAction('reset', customAction)"
                                >
                                    {{ customAction.label }}
                                </button>
                            </li>
                        </ul>
                    </Menu>
                </ButtonGroup>
                <Button v-else type="is-info" :disabled="processing" :compact @click="requestAction('reset')">
                    Reset To Draft
                </Button>
            </template>
            <Button v-if="deletable" type="is-danger" :disabled="processing" :compact @click="emit('delete')">
                Delete
            </Button>
        </template>

        <!-- If there's something to approve -->
        <template v-else-if="approvables[0]">
            <template v-if="approvables[0].has_outstanding_approval">
                <template v-if="!locked">
                    <template
                        v-for="item of [
                            {
                                type: 'is-success',
                                action: 'approve' as Action,
                                text: 'Approve',
                            },
                            {
                                type: 'is-danger',
                                action: 'reject' as Action,
                                text: 'Reject',
                            },
                        ]"
                        :key="item.action"
                    >
                        <ButtonGroup v-if="customActionsFor(item.action).length">
                            <Button
                                :type="item.type"
                                :title="
                                    !approvables[0].current_user_can_approve
                                        ? 'You are unable to approve this item'
                                        : undefined
                                "
                                :disabled="processing || !approvables[0].current_user_can_approve"
                                :compact
                                @click="requestAction(item.action)"
                            >
                                {{ item.text }}
                            </Button>
                            <Menu :triggers="['click']">
                                <template #trigger>
                                    <Button
                                        square
                                        :title="`More ${item.text.toLowerCase()} options`"
                                        :disabled="processing || !approvables[0].current_user_can_approve"
                                        :compact
                                    >
                                        <i class="mdi mdi-chevron-down"></i>
                                    </Button>
                                </template>
                                <ul>
                                    <li v-for="customAction in customActionsFor(item.action)" :key="customAction.label">
                                        <button
                                            :disabled="customAction.disabled"
                                            @click="requestCustomAction(item.action, customAction)"
                                        >
                                            {{ customAction.label }}
                                        </button>
                                    </li>
                                </ul>
                            </Menu>
                        </ButtonGroup>
                        <Button
                            v-else
                            :type="item.type"
                            :title="
                                !approvables[0].current_user_can_approve
                                    ? 'You are unable to approve this item'
                                    : undefined
                            "
                            :disabled="processing || !approvables[0].current_user_can_approve"
                            :compact
                            @click="requestAction(item.action)"
                        >
                            {{ item.text }}
                        </Button>
                    </template>
                </template>

                <span v-else-if="!approvables[0].current_user_can_approve" class="tag is-warning is-light">
                    Awaiting {{ approvables[0].has_approval_history ? 'Reapproval' : 'Approval' }}
                </span>

                <Button
                    v-if="isResetable"
                    type="is-info"
                    :disabled="processing"
                    :compact
                    @click="requestAction('reset')"
                >
                    Reset To Draft
                </Button>
            </template>

            <template v-else>
                <template v-if="approvables[0].is_approved">
                    <Tag :status="approvables[0].status" />
                    <Button
                        v-if="!locked"
                        type="is-info"
                        :disabled="processing"
                        :compact
                        @click="requestAction('reset')"
                    >
                        Reset To Draft
                    </Button>
                </template>

                <template v-else-if="approvables[0].is_rejected">
                    <Tag :status="approvables[0].status" />
                    <template v-if="!locked">
                        <Button type="is-info" :disabled="processing" :compact @click="requestAction('request')">
                            Request Reapproval
                        </Button>
                        <Button type="is-info" :disabled="processing" :compact @click="requestAction('reset')">
                            Reset To Draft
                        </Button>
                    </template>
                </template>

                <template v-else>
                    <Tag status="Draft" />
                    <Button
                        type="is-info"
                        :disabled="disableRequest || processing"
                        :compact
                        @click="requestAction('request')"
                    >
                        Request Approval
                    </Button>
                    <Tooltip v-if="approvables[0].current_user_can_approve" label="Approve Instantly">
                        <Button type="is-success" :disabled="processing" :compact @click="requestAction('approve')">
                            <i class="mdi mdi-check-bold" />
                        </Button>
                    </Tooltip>
                </template>
            </template>
        </template>

        <slot />

        <Modal
            ref="commentsModal"
            :title="'Add ' + action.charAt(0).toUpperCase() + action.slice(1) + ' Comments'"
            @closed="resetCommentsModal"
        >
            <FormField label="Comments">
                <Textbox v-model="comments" type="textarea" maxlength="255" class="tw-border-gray-600" />
            </FormField>

            <template #footer>
                <Button class="tw-ml-auto" @click="resetCommentsModal">Cancel</Button>
                <Button type="is-primary" :loading="submittingComments" :disabled="!comments" @click="postAction">
                    Submit
                </Button>
            </template>
        </Modal>
    </div>
</template>
<script setup lang="ts">
import Button from '@/js/components/controls/Button.vue';
import ButtonGroup from '@/js/components/controls/ButtonGroup.vue';
import Menu from '@/js/components/controls/Menu.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Modal from '@/js/components/Modal.vue';
import Tag from '@/js/components/Tag.vue';
import { ref, computed, useTemplateRef, type PropType } from 'vue';
import { route, toast } from '@/js/utils.js';
import { axios } from '@/js/utils/axios.ts';
import { Eventbus } from '@/js/utils/eventbus.ts';
import * as Sentry from '@sentry/vue';
import type { Approvable } from '@/js/types/approvable.ts';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

type Action = 'approve' | 'reject' | 'reset' | 'request';
type ActionPayload = Record<string, unknown>;
type CustomAction = {
    label: string;
    payload?: ActionPayload;
    disabled?: boolean;
};
type ApprovalControlApprovable = Approvable & {
    payruns?: Array<{ status: string }>;
    supplier_invoices?: Array<{ status: string }>;
};

const props = defineProps({
    approvables: {
        type: Array as PropType<Array<ApprovalControlApprovable>>,
        required: true,
    },
    bulkControls: {
        type: Boolean,
        default: false,
    },
    disableRequest: {
        type: Boolean,
        default: false,
    },
    locked: {
        type: Boolean,
        default: false,
    },
    hideRequest: {
        type: Boolean,
        default: false,
    },
    hideActions: {
        type: Boolean,
        default: false,
    },
    deletable: {
        type: Boolean,
        default: false,
    },
    compact: {
        type: Boolean,
        default: false,
    },
    customActions: {
        type: Object as PropType<Partial<Record<Action, CustomAction[]>>>,
        default: () => ({}),
    },
});
const emit = defineEmits(['reload', 'delete']);
const action = ref('');
const actionPayload = ref<ActionPayload>({});
const comments = ref('');
const processing = ref(false);
const commentsModal = useTemplateRef('commentsModal');
const submittingComments = ref(false);

const isResetable = computed(() => {
    if (!props.approvables[0]) {
        return false;
    }
    switch (props.approvables[0].approvable_type) {
        case 'App\\Models\\BudgetEntry':
            return !props.approvables[0].purchase_orders?.filter((po) =>
                ['Completed', 'Sent', 'Approved'].includes(po.status)
            );

        case 'App\\Models\\Payrun':
            return !props.approvables[0].payruns?.filter((pr) =>
                ['Awaiting Approval', 'Approved', 'Rejected'].includes(pr.status)
            );

        default:
            return true;
    }
});

const customActionsFor = (actionName: Action): CustomAction[] => props.customActions[actionName] ?? [];

const requestAction = (newAction: Action, payload: ActionPayload = {}) => {
    processing.value = true;
    action.value = newAction;
    actionPayload.value = payload;
    if (['reject', 'reset'].includes(action.value)) {
        commentsModal.value?.open();
        return;
    }

    postAction();
};

const requestCustomAction = (fallbackAction: Action, customAction: CustomAction) => {
    if (customAction.disabled) {
        return;
    }

    requestAction(fallbackAction, customAction.payload ?? {});
};

const resetCommentsModal = () => {
    commentsModal.value?.close();
    comments.value = '';
    action.value = '';
    actionPayload.value = {};
    processing.value = false;
};

const postAction = () => {
    const actionUrl = () => {
        switch (action.value) {
            case 'approve':
                return route('api.account.approvals.approve');
            case 'reject':
                return route('api.account.approvals.reject');
            case 'reset':
                return route('api.account.approvals.reset');
            case 'request':
                return route('api.account.approvals.request');
            default:
                return null;
        }
    };
    if (!actionUrl()) {
        toast('Error', `The action "${action.value}" is not supported`, 'error');
        Sentry.captureMessage('Unsupported approval action attempted', {
            level: 'error',
            extra: {
                action: action.value,
                approvables: props.approvables,
            },
        });
        return;
    }
    if (!props.approvables.length) {
        toast(
            'Error',
            'It looks like an internal error has occurred. This has been reported and try again later',
            'error'
        );
        Sentry.captureMessage('Attempted to perform approval action without any approvables', {
            level: 'error',
            extra: {
                action: action.value,
                approvables: props.approvables,
            },
        });
        return;
    }
    submittingComments.value = true;
    axios
        .post(actionUrl(), {
            approvables: props.approvables,
            comments: comments.value,
            ...actionPayload.value,
        })
        .then(() => {
            const eventNameByActionAndType: Partial<Record<Action, Record<string, string>>> = {
                request: {
                    'App\\Models\\BudgetEntry': 'budget_entry_approval_requested',
                    'App\\Models\\PurchaseOrder': 'purchase_order_approval_requested',
                    'App\\Models\\Payrun': 'payrun_approval_requested',
                },
                approve: {
                    'App\\Models\\BudgetEntry': 'budget_entry_approved',
                    'App\\Models\\PurchaseOrder': 'purchase_order_approved',
                },
                reject: {
                    'App\\Models\\BudgetEntry': 'budget_entry_rejected',
                    'App\\Models\\PurchaseOrder': 'purchase_order_rejected',
                },
                reset: {
                    'App\\Models\\BudgetEntry': 'budget_entry_reset_to_draft',
                    'App\\Models\\PurchaseOrder': 'purchase_order_reset_to_draft',
                },
            };
            const eventNamesByType = eventNameByActionAndType[action.value as Action];
            props.approvables.forEach((approvable) => {
                const approvableType = approvable.approvable_type?.split('\\').pop() ?? 'unknown';
                if (action.value === 'request') {
                    capturePosthogEvent('approval_requested', { approvable_type: approvableType });
                } else if (action.value === 'approve' || action.value === 'reject') {
                    capturePosthogEvent('approval_actioned', {
                        approvable_type: approvableType,
                        decision: action.value,
                    });
                }
                if (comments.value.trim()) {
                    capturePosthogEvent('approval_comment_added', { approvable_type: approvableType });
                }

                const eventName = approvable.approvable_type
                    ? eventNamesByType?.[approvable.approvable_type]
                    : undefined;
                if (eventName) {
                    capturePosthogEvent(eventName);
                }
            });
            reloadApprovable();
            resetCommentsModal();
        })
        .finally(() => {
            submittingComments.value = false;
        });
};
const reloadApprovable = () => {
    if (!props.approvables[0]) {
        processing.value = false;
        return;
    }
    emit('reload');
    switch (props.approvables[0].approvable_type) {
        case 'App\\Models\\BudgetEntry':
            if (props.approvables.length === 1 && !props.bulkControls) {
                Eventbus.$emit('budget:entry', {
                    entry: props.approvables[0].id,
                    event: props.approvables[0].event_id,
                });
            }

            Eventbus.$emit('reload:entries');
            break;
        case 'App\\Models\\PurchaseOrder':
            if (props.approvables.length === 1 && !props.bulkControls) {
                Eventbus.$emit('purchaseOrder:reloadLineItems');
            }

            Eventbus.$emit('reload:purchaseorders');
            break;
        case 'App\\Models\\Payrun':
            if (props.approvables.length === 1 && !props.bulkControls) {
                Eventbus.$emit('reload:payrun');
            }

            Eventbus.$emit('reload:payruns');
            break;
    }

    Eventbus.$emit('refresh:approvals');

    setTimeout(() => {
        processing.value = false;
    }, 300);
};
</script>
